/*
 * FolderManager — Folder-Hierarchie fuer das Project Dashboard (Phase 4)
 *
 * Verantwortlich fuer:
 * - CRUD auf Folders (in IndexedDB)
 * - Tree-Operationen (Pfad-Auflösung, Descendants, Zyklus-Schutz)
 * - Sync-Integration: Folder-Aenderungen werden in den SyncService enqueued,
 *   der sie im naechsten Sync-Batch mit dem Server abgleicht.
 *
 * **Folder-Loeschung** ist hier bewusst nur Folder-Operation. Die Spec sagt:
 * Beim Folder-Delete-All werden alle Projekte des Unterbaums in den Trash
 * geschoben (`ProjectManager.softDeleteProject`), und DANACH wird der
 * Folder hart geloescht. Diese Orchestrierung macht der Dashboard-Code,
 * nicht der FolderManager — der ist nur Daten-Plumbing.
 *
 * Siehe Spec: `docs/project-dashboard-specification.md` §2.1, §5.2, §6.
 */

import { generateUUID } from './uuid.js'

export class FolderManager {
    /**
     * @param {import('./IndexedDBStore.js').IndexedDBStore} store
     * @param {() => string|null} getUserId - Liefert die aktuelle User-ID (oder null wenn anonym).
     *                                        Cloud-User: echte User-UUID. Anonym: null.
     */
    constructor(store, getUserId) {
        this._store = store
        this._getUserId = getUserId
        this._syncService = null  // wird via setSyncService() injiziert (Bootstrap-Order)
    }

    /**
     * @param {import('./SyncService.js').SyncService|null} syncService
     */
    setSyncService(syncService) {
        this._syncService = syncService
    }

    // ─────────────────────────────────────────────
    // CRUD
    // ─────────────────────────────────────────────

    /**
     * Legt einen neuen Folder an.
     * @param {string} name - Anzeigename (1..200 Zeichen, leerer Name wird zu "Untitled")
     * @param {string|null} parentFolderId - Parent (null = Root)
     * @returns {Promise<Object>} Das neu angelegte Folder-Objekt
     */
    async createFolder(name, parentFolderId = null) {
        const id = generateUUID()
        const now = new Date().toISOString()
        const folder = {
            id,
            userId: this._getUserId(),
            parentFolderId: parentFolderId || null,
            name: (name || '').trim() || 'Untitled',
            version: 1,
            createdAt: now,
            updatedAt: now,
            deletedAt: null
        }
        await this._store.putFolder(folder)
        this._enqueueFolderChange({ type: 'upsert', folder })
        return folder
    }

    /**
     * Benennt einen Folder um.
     * @param {string} folderId
     * @param {string} newName
     * @returns {Promise<Object|null>} Aktualisierter Folder oder null wenn nicht gefunden
     */
    async renameFolder(folderId, newName) {
        const folder = await this._store.getFolder(folderId)
        if (!folder) return null
        folder.name = (newName || '').trim() || 'Untitled'
        folder.version = (folder.version || 1) + 1
        folder.updatedAt = new Date().toISOString()
        await this._store.putFolder(folder)
        this._enqueueFolderChange({ type: 'upsert', folder })
        return folder
    }

    /**
     * Verschiebt einen Folder unter einen neuen Parent.
     *
     * **Zyklus-Schutz**: Ein Folder kann nicht in sich selbst oder einen
     * seiner Descendants verschoben werden — sonst entsteht ein Kreis.
     * Der PathPicker filtert Self+Descendants vorab aus der Auswahl raus,
     * aber Drag&Drop ist freier, darum hier nochmal hart gepruefte
     * Defense-in-Depth.
     *
     * @param {string} folderId
     * @param {string|null} newParentFolderId
     * @returns {Promise<{ok: true, folder: Object} | {ok: false, reason: string}>}
     */
    async moveFolder(folderId, newParentFolderId) {
        if (folderId === newParentFolderId) {
            return { ok: false, reason: 'cycle' }
        }
        const folder = await this._store.getFolder(folderId)
        if (!folder) return { ok: false, reason: 'not_found' }

        if (newParentFolderId) {
            const isDescendant = await this._isDescendant(newParentFolderId, folderId)
            if (isDescendant) return { ok: false, reason: 'cycle' }
        }

        folder.parentFolderId = newParentFolderId || null
        folder.version = (folder.version || 1) + 1
        folder.updatedAt = new Date().toISOString()
        await this._store.putFolder(folder)
        this._enqueueFolderChange({ type: 'upsert', folder })
        return { ok: true, folder }
    }

    /**
     * Loescht einen Folder hart (kein Soft-Delete). Cascade auf Sub-Folder
     * und Projekt-Auswirkungen sind NICHT Teil dieser Methode — der
     * Aufrufer (Dashboard) ist verantwortlich, vorher die Projekte in den
     * Trash zu schieben (`ProjectManager.softDeleteProject`) und Sub-Folder
     * rekursiv mit `deleteFolder` zu loeschen.
     *
     * @param {string} folderId
     */
    async deleteFolder(folderId) {
        await this._store.deleteFolder(folderId)
        this._enqueueFolderChange({ type: 'delete', folderId })
    }

    // ─────────────────────────────────────────────
    // Tree-Operationen
    // ─────────────────────────────────────────────

    /**
     * Liefert alle aktiven Folder des Users.
     * @returns {Promise<Object[]>}
     */
    async getAllFolders() {
        return this._store.getAllFolders(this._getUserId())
    }

    /**
     * Liefert die direkten Kinder eines Folders (Sub-Folder, eine Ebene).
     * @param {string|null} parentFolderId - null = Root-Children
     * @returns {Promise<Object[]>}
     */
    async getChildFolders(parentFolderId) {
        const all = await this.getAllFolders()
        return all.filter(f => (f.parentFolderId || null) === (parentFolderId || null))
    }

    /**
     * Liefert die Folder-IDs aller Descendants (rekursiv, inklusive `folderId` selbst).
     * Nutzt einen In-Memory-Tree-Walk: ein einziger getAll-Roundtrip, dann
     * Breadth-First-Traversal. O(N) ueber alle User-Folder.
     *
     * @param {string} folderId
     * @returns {Promise<string[]>} - inklusive `folderId` selbst
     */
    async getDescendantFolderIds(folderId) {
        const all = await this.getAllFolders()
        const childrenByParent = new Map()
        for (const f of all) {
            const p = f.parentFolderId || null
            if (!childrenByParent.has(p)) childrenByParent.set(p, [])
            childrenByParent.get(p).push(f.id)
        }
        const result = [folderId]
        const queue = [folderId]
        while (queue.length > 0) {
            const cur = queue.shift()
            const kids = childrenByParent.get(cur) || []
            for (const kid of kids) {
                result.push(kid)
                queue.push(kid)
            }
        }
        return result
    }

    /**
     * Liefert den Pfad eines Folders als Array von Folder-Objekten,
     * vom Root absteigend bis zum Folder selbst. Leeres Array fuer Root.
     *
     * @param {string|null} folderId - null/undefined = Root → leeres Array
     * @returns {Promise<Object[]>}
     */
    async getFolderPath(folderId) {
        if (!folderId) return []
        const path = []
        let current = await this._store.getFolder(folderId)
        const guard = new Set()  // Zyklus-Defense
        while (current && !guard.has(current.id)) {
            guard.add(current.id)
            path.unshift(current)
            if (!current.parentFolderId) break
            current = await this._store.getFolder(current.parentFolderId)
        }
        return path
    }

    // ─────────────────────────────────────────────
    // Sync-Integration
    // ─────────────────────────────────────────────

    /**
     * Wird beim Sync-Pull aufgerufen — uebernimmt einen Folder-Eintrag
     * vom Server (Last-Write-Wins ueber Version). Kein Re-Enqueue, weil
     * die Aenderung vom Server kommt.
     *
     * **userId-Hydration**: Die Server-Response enthaelt kein userId-Feld
     * (der Server filtert sowieso pro Session). Der lokale Store dagegen
     * filtert in `getAllFolders(userId)` ueber das userId-Feld — ohne
     * dieses Feld waeren server-gepullte Folder unsichtbar. Wir spiegeln
     * deshalb dieselbe Logik wie bei Projects (SyncService initialSync):
     * userId vom aktiven User uebernehmen, falls der Server keinen
     * mitgeliefert hat.
     *
     * @param {Object} serverFolder
     */
    async applyServerFolder(serverFolder) {
        const local = await this._store.getFolder(serverFolder.id)
        if (!local || (serverFolder.version || 1) > (local.version || 1)) {
            const merged = {
                ...serverFolder,
                userId: serverFolder.userId || this._getUserId() || null
            }
            await this._store.putFolder(merged)
        }
    }

    /**
     * Wird beim Sync-Pull aufgerufen — entfernt einen Folder lokal,
     * weil der Server ihn nicht mehr hat.
     * @param {string} folderId
     */
    async applyServerFolderDelete(folderId) {
        await this._store.deleteFolder(folderId)
    }

    // ─────────────────────────────────────────────
    // Private
    // ─────────────────────────────────────────────

    /**
     * Prueft, ob `candidateAncestorId` ein Vorfahre von `descendantId` ist
     * (oder gleich). Genutzt fuer Zyklus-Schutz beim moveFolder.
     * @param {string} candidateAncestorId
     * @param {string} descendantId
     * @returns {Promise<boolean>}
     */
    async _isDescendant(candidateAncestorId, descendantId) {
        const ancestors = await this.getDescendantFolderIds(descendantId)
        return ancestors.includes(candidateAncestorId)
    }

    /**
     * Schiebt eine Folder-Aenderung in den SyncService (falls verfuegbar).
     * @param {{type: 'upsert', folder: Object} | {type: 'delete', folderId: string}} change
     */
    _enqueueFolderChange(change) {
        if (!this._syncService) return
        if (typeof this._syncService.enqueueFolderChange === 'function') {
            this._syncService.enqueueFolderChange(change)
        }
    }
}
